import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TablePriceDisplay } from '@/components/ui-v2/inputs/table/table-price-display';
import { TablePriceInput } from '@/components/ui-v2/inputs/table/table-price-input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsCellEditing } from '@/hooks/use-is-cell-editing';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import {
  DealItem,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import { formatCurrency, getVatLabelKey } from '@/lib/utils';
import { rangeFilterFn } from '@/lib/utils/table-filter-utils';
import { useSetColumnSort } from '@/states/column-sort.state';
import { storeAtom } from '@/states/store.state';
import { Row } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { InfoIcon } from 'lucide-react';

import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';
import { TableCellWithRecentlyModified } from './table-cell-with-recently-modified';

interface ColumnProps {
  handleCellClick: (id: string, field: keyof DealItem) => void;
  handleCellEdit: <K extends keyof DealItem>(
    id: string | undefined,
    field: K,
    value: DealItem[K],
    isLeafRow?: boolean
  ) => Promise<void>;
  handleKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  handleBlur: React.FocusEventHandler<HTMLInputElement>;
}

interface DiscountedPriceCellContentProps {
  row: Row<DealItemWithVersions>;
  handleCellClick: ColumnProps['handleCellClick'];
  handleCellEdit: ColumnProps['handleCellEdit'];
  handleKeyDown: ColumnProps['handleKeyDown'];
  handleBlur: ColumnProps['handleBlur'];
}

function DiscountedPriceCellContent({
  row,
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
  handleBlur,
}: DiscountedPriceCellContentProps) {
  const store = useAtomValue(storeAtom);
  const isOfferLocked = useIsOfferLocked();
  const isEditing = useIsCellEditing(row, 'discountedPrice');
  const isParent = row.subRows.length > 1;

  const getValue = () => {
    const value = row.original.activeVersion.discountedPrice?.toFixed(2);
    if (typeof value === 'undefined' || value === null) return '—';
    return formatCurrency(parseFloat(value), store);
  };

  const getParentValue = () => {
    const subRowsValues: number[] = [];
    row.subRows.forEach((subRow) => {
      const value = subRow.original.activeVersion.discountedPrice?.toFixed(2);
      if (typeof value === 'undefined' || value === null) return;
      subRowsValues.push(parseFloat(value));
    });
    if (subRowsValues.length === 0) return '—';
    const min = formatCurrency(Math.min(...subRowsValues), store);
    const max = formatCurrency(Math.max(...subRowsValues), store);
    return min === max ? max : `${min} - ${max}`;
  };

  return (
    <TableCellWithRecentlyModified
      productId={row.original.id}
      field="discountedPrice"
      isParent={isParent}
    >
      {(isRecentlyModified) => (
        <div className="h-full w-full">
          {isEditing ? (
            <TablePriceInput
              value={row.original.activeVersion.discountedPrice ?? null}
              onChange={async (value) => {
                await handleCellEdit(
                  row.original.activeVersion.id,
                  'discountedPrice',
                  value,
                  !isParent
                );
              }}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              disabled={isOfferLocked}
              autoFocus={isEditing}
              hasError={false}
            />
          ) : (
            <TablePriceDisplay
              value={isParent ? getParentValue() : getValue()}
              isEmpty={false}
              hasError={false}
              isEditable={!isOfferLocked}
              isParent={isParent}
              isRecentlyModified={isRecentlyModified}
              onClick={() =>
                !isParent &&
                handleCellClick(
                  row.original.activeVersion.id,
                  'discountedPrice'
                )
              }
            />
          )}
        </div>
      )}
    </TableCellWithRecentlyModified>
  );
}

const useColumnDefDiscountedPrice = ({
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
  handleBlur,
}: ColumnProps): ExtendedColumnDef<DealItemWithVersions> => {
  const { t } = useTranslation();
  const store = useAtomValue(storeAtom);
  const { setColumnSort } = useSetColumnSort();

  const accessorKey: keyof DealItem = 'discountedPrice';
  return useMemo(
    () => ({
      accessorKey: accessorKey,
      name: 'discountedPrice',
      filterFn: rangeFilterFn,
      accessorFn: (row) => row.activeVersion?.discountedPrice ?? 0,
      aggregationFn: 'mean',
      sortingFn: 'basic',
      header: () => (
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <InfoIcon className="h-4 w-4" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('kpi.discounted_price_tooltip')}</p>
            </TooltipContent>
          </Tooltip>{' '}
          <div
            className="flex flex-1 items-center gap-2"
            onClick={() => setColumnSort(accessorKey)}
          >
            {t('kpi.discounted_price')} ({t(getVatLabelKey(store))})
            <SortIcon pointer="discountedPrice" />
          </div>
        </div>
      ),
      cell: ({ row }) => (
        <DiscountedPriceCellContent
          row={row}
          handleCellClick={handleCellClick}
          handleCellEdit={handleCellEdit}
          handleKeyDown={handleKeyDown}
          handleBlur={handleBlur}
        />
      ),
      size: 180,
    }),
    [
      t,
      store,
      setColumnSort,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
      handleBlur,
    ]
  );
};

export default useColumnDefDiscountedPrice;
